// src/pages/Landing.jsx
import React, { useState } from "react";
import TrustpilotReviews from "../components/TrustpilotReviews";
import { normalizeApiUrl } from "../lib/api";
import { textOnlyOptions, formatIntl } from "../lib/countries";
import NewsHomeCarousel from "../components/NewsHomeCarousel.jsx";
import Container from "../components/Container.jsx";
import useGuidesList from "../hooks/useGuidesList";
import { openCalendlyPopup, openCalendlyPopupNoPrefill } from "../lib/calendly";

/**
 * Landing (Direct Marketing)
 * - Hero with image + gradient overlay
 * - Form card (GuideDownload parity)
 * - Carousel, benefits, Trustpilot, final CTA
 * - Writes to /enquire (route="landing", source="Direct Marketing")
 * - Triggers guide 1002 download
 */

const GUIDE_ID = "1002";
const PAGE_TAG = "LandingPageMain";

// Remove +CC / 00CC and any leading zero the browser might insert
function scrubLocal(raw = "", cc = "+44") {
  let v = String(raw).replace(/[\s-]+/g, "");
  const ccNum = String(cc || "+").replace("+", "");
  if (ccNum) {
    const re = new RegExp(`^(?:\\+|00)?${ccNum}\\s*0?`);
    v = v.replace(re, "");
  }
  return v.replace(/^\+/, "");
}

export default function Landing() {
  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+44");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [readyToInvest, setReadyToInvest] = useState("");
  const [amountToInvest, setAmountToInvest] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);

  const guides = useGuidesList();

  function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());
}

function isValidPhone(phone) {
  // accept 7–15 digits, optional + prefix
  return /^\+?\d{7,15}$/.test(String(phone).replace(/\s+/g, ""));
}

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    setLoading(true);

      const cleanEmail = email.trim().toLowerCase();
  const cleanPhone = phoneLocal.replace(/\s+/g, "");

  if (!isValidEmail(cleanEmail)) {
    setErr("Please enter a valid email address (e.g. name@example.com).");
    return;
  }
  if (!isValidPhone(cleanPhone)) {
    setErr("Please enter a valid phone number.");
    return;
  }

    try {
      if (!fullName || !email || !phoneLocal || !consent) {
        throw new Error("Please complete required fields.");
      }

      // Clean local & format international (matches GuideDownload behaviour)
      const phoneLocalClean = scrubLocal(phoneLocal, countryCode);
      const phoneIntl = formatIntl(countryCode, phoneLocalClean);

      const cleanEmail = (email || "").trim().toLowerCase();


      // SAME WRITE PATH as GuideDownload: POST /enquire (URL-encoded)
      const sheetData = {
        name: fullName,
        email: cleanEmail,
        countryCode,
        phone: phoneIntl,
        message,
        readyToInvest,
        amountToInvest,
        consent: consent ? "yes" : "no",
        timestamp: new Date().toISOString(),
        route: "landing", // -> Apps Script "Landing" tab
        source: "Direct Marketing", // -> Zoho Lead_Source

        "Eguide Downloaded": PAGE_TAG,
      };

      const res = await fetch("/enquire", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams(sheetData),
      });

      const text = await res.text();
      let ok = res.ok,
        serverErr = "";
      try {
        const j = JSON.parse(text);
        if (typeof j?.ok === "boolean") ok = j.ok;
        if (j?.error) serverErr = j.error;
      } catch {
        /* non-JSON fallback */
      }
      if (!ok) throw new Error(serverErr || "Submit failed");

      // Start download (same approach as GuideDownload)
      const finalLink = `/api/guides/download?guide=${encodeURIComponent(
        GUIDE_ID
      )}`;
      const url = await normalizeApiUrl(finalLink);

      try {
        const r = await fetch(url);
        if (!r.ok) throw new Error();
        const blob = await r.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = "guide-1002.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch {
        window.open(url, "_blank", "noopener");
      }

      // Optional parity tracking
      window.gtag?.("event", "file_download", {
        file_name: "guide-1002.pdf",
        file_type: "pdf",
        link_domain: window.location.hostname,
        guide_id: GUIDE_ID,
      });
      window.fbq?.("trackCustom", "GuideDownload", {
        guide: GUIDE_ID,
        title: GUIDE_ID,
      });

      setSuccess(true);
    } catch (e2) {
      setErr(e2.message || "Submit error");
    } finally {
      setLoading(false);
    }
  }

  // ✅ Gate Calendly to the form + map a1/a2/a3 in the right order
  function openCalendlyFromForm() {
    if (!fullName || !email || !phoneLocal || !consent) {
      setErr(
        "Please complete Name, Email, Phone and consent before booking."
      );
      // focus first missing field (nice UX)
      const firstMissing =
        (!fullName && "input[autocomplete='name']") ||
        (!email && "input[autocomplete='email']") ||
        (!phoneLocal && "input[name='phoneLocal']") ||
        null;
      if (firstMissing) document.querySelector(firstMissing)?.focus();
      return;
    }

    const phoneLocalClean = scrubLocal(phoneLocal, countryCode);
    const phoneIntl = formatIntl(countryCode, phoneLocalClean);

    // Calendly custom question order:
    // a1 -> Phone, a2 -> When planning, a3 -> Amount
    openCalendlyPopup({
      name: fullName,
      email,
      a1: phoneIntl, // Phone (custom question #1)
      a2: readyToInvest, // When are you planning to invest? (#2)
      a3: amountToInvest, // Amount to invest (#3)
      utm: { utmSource: "landing", utmMedium: "cta", utmCampaign: "hero" },
    });
  }

  const heroImage = "content/images/hero-image.jpg"; // update to your path
   const testimonial1_1080 = "/content/videos/testimonial1_1080.mp4";
  const testimonial2_1080 = "/content/videos/testimonial2_1080.mp4";
  const poster1 = "content/images/Screenshot 2025-11-14 at 10.01.35.png";
const poster2 = "content/images/Screenshot 2025-11-14 at 10.00.56.png";

  return (
    <main className="bg-white">
      {/* HERO — image with overlay + value props + form card */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${heroImage}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#003366]/80 via-[#003366]/60 to-white/0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-10 items-start">

            {/* Left copy */}
            <div className="text-white lg:col-start-1 lg:row-start-1">
              <h1 className="text-3xl sm:text-4xl font-semibold">
                Property investing made clear and confident
              </h1>
              <p className="mt-3 max-w-xl text-white/90 text-2xl">
                Get practical insights and personalised support to move from
                research to results. Download the guide or book a call to begin.
              </p>
              <ul className="mt-5 space-y-2 text-white/90 text-2xl">
                <li>• Data-led decisions</li>
                <li>• Expert, independent advice</li>
                <li>• End-to-end support</li>
              </ul>
            </div>

            {/* Right form card */}
            <div className="rounded-2xl bg-white/95 backdrop-blur border border-slate-200 p-5 sm:p-6 shadow-xl w-full max-w-md mx-auto lg:mx-0 lg:col-start-2 lg:row-start-1">
              <h2 className="text-xl font-semibold text-[#003366]">
                Download the guide
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Fill in your details to download instantly.
              </p>

              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    required
                    className="h-11 rounded border px-3"
                    placeholder="Full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                  />
                  <input
                    required
                    type="email"
                    name="email"
                    className="h-11 rounded border px-3"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>

                <div>
  <label className="block text-sm font-medium text-[#003366]">Phone</label>
<div className="mt-1 flex flex-col sm:flex-row gap-2 items-stretch">
  <select
    name="countryCode"
    required
    value={countryCode}
    onChange={(e) => {
      const cc = e.target.value;
      setCountryCode(cc);
      setPhoneLocal((prev) => scrubLocal(prev, cc));
    }}
    className="rounded border px-3 py-2 h-11 w-full sm:w-32 md:w-40 shrink-0"
    disabled={loading}
    autoComplete="tel-country-code"
  >
    {textOnlyOptions().map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>

  <input
    name="phoneLocal"
    type="tel"
    value={phoneLocal}
    onChange={(e) => setPhoneLocal(scrubLocal(e.target.value, countryCode))}
    onPaste={(e) => {
      const t = (e.clipboardData || window.clipboardData).getData("text");
      setPhoneLocal(scrubLocal(t, countryCode));
      e.preventDefault();
    }}
    className="rounded border px-3 py-2 h-11 w-full flex-1 min-w-0"
    placeholder="Phone"
    autoComplete="tel-national"
    inputMode="numeric"
    pattern="[0-9]*"
    required
    disabled={loading}
  />
</div>

</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={readyToInvest}
                    onChange={(e) => setReadyToInvest(e.target.value)}
                    className="h-11 rounded border px-3"
                    required
                  >
                    <option value="">
                      When are you planning to make your next investment?
                    </option>
                    <option>Ready to invest now.</option>
                    <option>Within 3 months.</option>
                    <option>3-6 months.</option>
                    <option>Just exploring for now.</option>
                  </select>
                  <select
                    value={amountToInvest}
                    onChange={(e) => setAmountToInvest(e.target.value)}
                    className="h-11 rounded border px-3"
                    required
                  >
                    <option value="">
                      What level of funds are you planning to allocate?
                    </option>
                    <option>Under £100,000</option>
                    <option>£100,000 - £250,000</option>
                    <option>£250,000 - £500,000</option>
                    <option>£500,000 plus</option>
                    <option>Unknown</option>
                  </select>
                </div>

                <div>
  <label className="block text-sm font-medium text-[#003366]">Message (optional)</label>
  <textarea
    name="message"
    value={message}
    onChange={(e) => setMessage(e.target.value)}
    rows={3}
    placeholder="Any specific questions or goals you'd like to discuss?"
    className="mt-1 w-full rounded border px-3 py-2 text-sm"
    disabled={loading}
  />
</div>


                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  I agree to be contacted about this guide and related services.
                </label>

                {err && <p className="text-sm text-red-600">{err}</p>}
                {success && (
                  <div className="p-3 rounded bg-green-50 border border-green-200 text-green-700">
                    <strong>Thanks — your guide is downloading now.</strong>
                    <p className="text-sm">
                      One of the team may contact you to discuss next steps. Or
                      book a call if you'd like immediate help.
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="h-11 px-6 rounded bg-[#003366] text-white"
                  >
                    {loading ? "Submitting…" : "Get your guide now"}
                  </button>

                  {/* ✅ Calendly: gated + prefilled with phone, timing, amount */}
                  <button
                    type="button"
                    onClick={openCalendlyFromForm}
                    disabled={
                      !fullName || !email || !phoneLocal || !consent || loading
                    }
                    className="inline-flex items-center px-4 rounded border border-[#003366] text-[#003366] disabled:opacity-50"
                    aria-label="Book a call on Calendly"
                  >
                    Book a call
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WORK WITH US */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-6 sm:pt-10 sm:pb-8">
        <h2 className="text-2xl font-semibold text-[#003366]">
          Why work with us
        </h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Independent</h3>
            <p className="mt-2 text-sm text-slate-700">
              We’re not tied to a single vendor, or market, so we recommend what suits
              your plan - not ours.
            </p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Bespoke guidance</h3>
            <p className="mt-2 text-sm text-slate-700">
              1:1 advice based on your goals, timeframe and risk appetite -
              backed by experience, and a well established network. No guesswork.
            </p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">360° service</h3>
            <p className="mt-2 text-sm text-slate-700">
              Finance, legals, surveys, furnishing and lets - a joined-up
              journey from start to finish.
            </p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Trusted by investors</h3>
            <p className="mt-2 text-sm text-slate-700">
              From first-time buyers to institutional investors - long-term
              relationships and measurable outcomes.
            </p>
          </div>
        </div>
      </section>

            {/* CLIENT TESTIMONIAL VIDEOS */}
<section className="max-w-6xl mx-auto px-6 pt-8 pb-4 sm:pt-10 sm:pb-6">
  <h2 className="text-2xl font-semibold text-[#003366]">Client stories</h2>
  <p className="mt-1 text-sm text-slate-600">Hear how investors work with Ethira.</p>

  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
    <video
      className="w-full rounded-xl border shadow-sm"
      controls
      playsInline
      preload="none"
      poster={poster1}
    >
      <source src={testimonial1_1080} type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    <video
      className="w-full rounded-xl border shadow-sm"
      controls
      playsInline
      preload="none"
      poster={poster2}
    >
      <source src={testimonial2_1080} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </div>
</section>


      {/* TRUSTPILOT */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-8 sm:pt-8 sm:pb-10">
        <TrustpilotReviews />
      </section>

      {/* WHY THIS STRATEGY */}
      <section className="max-w-6xl mx-auto px-6 py-8 sm:py-10">
        <h2 className="text-2xl font-semibold text-[#003366]">
          Why this strategy?
        </h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">
              Inflation-aware returns
            </h3>
            <p className="mt-2 text-sm text-slate-700">
              We focus on assets and structures that protect purchasing power
              over time, with the aim of stable, compounding returns across
              cycles.
            </p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Hands-off ownership</h3>
            <p className="mt-2 text-sm text-slate-700">
              From sourcing to completion and lettings, we coordinate the moving
              parts so your investment stays simple.
            </p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">
              Institutional discipline
            </h3>
            <p className="mt-2 text-sm text-slate-700">
              Clear criteria, verifiable numbers and stress-tested scenarios - no
              hype, just data-led decision making.
            </p>
          </div>
        </div>
      </section>

      {/* Insights & e-guides carousel */}
      <section className="pt-16 sm:pt-24 pb-8 sm:pb-10 bg-slate-50">
        <Container>
          <div className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003366]">
              Insights & e-guides
            </h2>
            <p className="mt-3 text-[#003366]">
              Independent analysis and downloadable resources designed to help
              you make informed decisions.
            </p>
          </div>
          <NewsHomeCarousel items={guides} />
        </Container>
      </section>

      {/* FINAL CTA */}
      {/* FINAL CTA */}
<section className="bg-slate-50">
  <div className="max-w-6xl mx-auto px-6 py-10 sm:py-12">
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-[#003366]">Ready to achieve your financial goals?</h3>
            <p className="text-sm text-slate-700 mt-1">Book a quick call to speak to one of our qualified financial experts, and start the journey towards financial freedom!</p>
          </div>

          <div className="flex w-full md:w-auto gap-3 md:justify-end">
            <button
              type="button"
              onClick={() =>
                openCalendlyPopupNoPrefill({
                  utm: { utmSource: "landing", utmMedium: "cta", utmCampaign: "footer-primary" },
                })
              }
              className="inline-flex h-11 w-full md:w-auto items-center justify-center rounded bg-[#003366] px-5 text-white"
            >
              Book a call
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>


      {/* COMPLIANCE NOTE */}
      <section className="max-w-6xl mx-auto px-6 pb-10 text-xs text-slate-500">
        Capital at risk. Past performance is not a reliable indicator of future
        results. This page is for information only and does not constitute
        regulated financial advice.
      </section>
    </main>
  );
}
